import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { SessionStep } from "@/hooks/useSessionSteps";
import { ChevronDown, ChevronRight, Pencil, Lock, Crown, Shield, Eye } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { formatDistanceToNow } from "date-fns";
import { de } from "date-fns/locale";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface SessionHistoryProps {
  steps: SessionStep[];
  isLoading: boolean;
  onStepClick: (step: SessionStep) => void;
  myRole?: string | null;
  onStepSeen?: (stepId: string) => void;
}

const roleIcons: Record<string, React.ReactNode> = {
  owner: <Crown className="h-3 w-3 text-amber-500" />,
  contributor: <Pencil className="h-3 w-3 text-primary" />,
  reviewer: <Shield className="h-3 w-3 text-emerald-500" />,
  viewer: <Eye className="h-3 w-3 text-muted-foreground" />,
};

function canEditStep(step: SessionStep, userId: string | undefined, myRole: string | null | undefined): boolean {
  if (!userId || !myRole) return false;
  if (myRole === 'owner') return true;
  if (myRole === 'contributor' && step.created_by === userId) return true;
  return false;
}

export const SessionHistory = ({ steps, isLoading, onStepClick, myRole, onStepSeen }: SessionHistoryProps) => {
  const { user } = useAuth();
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());

  const toggleStep = (stepId: string) => {
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(stepId)) {
        next.delete(stepId);
      } else {
        next.add(stepId);
        // Mark as seen when user expands the step
        onStepSeen?.(stepId);
      }
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="text-sm text-muted-foreground">Lade Historie...</div>
      </div>
    );
  }

  if (steps.length === 0) {
    return (
      <div className="p-6">
        <div className="text-sm text-muted-foreground">
          Noch keine Antworten vorhanden
        </div>
      </div>
    );
  }

  return (
    <ScrollArea className="h-[calc(100vh-8rem)]">
      <div className="p-6 space-y-4">
        <h2 className="text-lg font-semibold text-foreground mb-4">
          Session Historie
        </h2>
        {steps.map((step) => {
          const isExpanded = expandedSteps.has(step.id);
          const editable = canEditStep(step, user?.id, myRole);
          const wasEdited = step.updated_at && step.created_at && step.updated_at !== step.created_at;

          return (
            <div
              key={step.id}
              className="border border-border rounded-lg bg-card transition-colors hover:bg-accent/5"
            >
              <div className="flex flex-col">
                <button
                  onClick={() => toggleStep(step.id)}
                  className="w-full p-4 text-left flex items-start gap-3 hover:bg-accent/10 transition-colors"
                >
                  <div className="mt-1">
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <Badge variant="outline" className="text-xs">
                        Schritt {step.step_number}
                      </Badge>
                      {step.isNew && (
                        <Badge variant="default" className="text-[10px] px-1.5 py-0 h-4 animate-pulse">
                          Neu
                        </Badge>
                      )}
                      <Badge variant="secondary" className="text-xs">
                        {step.subphase}
                      </Badge>
                      {step.clarity_score !== null && (
                        <Badge variant="outline" className="text-xs">
                          Klarheit: {step.clarity_score}%
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm font-medium text-foreground line-clamp-2">
                      {step.question_text}
                    </p>
                    {/* Attribution line */}
                    <div className="flex items-center gap-1.5 mt-1.5 text-[11px] text-muted-foreground">
                      {step.creator_role && roleIcons[step.creator_role]}
                      <span>
                        von {step.creator_name || "Unbekannt"}
                        {step.creator_role && ` (${step.creator_role})`}
                      </span>
                      {wasEdited && (
                        <span className="ml-1">
                          · bearbeitet {formatDistanceToNow(new Date(step.updated_at), { locale: de, addSuffix: true })}
                        </span>
                      )}
                    </div>
                  </div>
                </button>

                {isExpanded && (
                  <div className="mx-4 mb-3 flex items-center gap-2">
                    {editable ? (
                      <button
                        onClick={() => onStepClick(step)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-primary hover:text-primary/80 border border-primary/20 rounded-md hover:bg-primary/5 transition-colors"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        Bearbeiten
                      </button>
                    ) : (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-muted-foreground border border-border rounded-md cursor-not-allowed opacity-60">
                            <Lock className="h-3.5 w-3.5" />
                            Nur lesen
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Du kannst nur eigene Beiträge als Contributor bearbeiten, oder alle als Owner.</p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                )}
              </div>
              {isExpanded && (
                <div className="px-4 pb-4 space-y-3 border-t border-border pt-3">
                  {step.user_answer && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">
                        Antwort:
                      </p>
                      <p className="text-sm text-foreground">
                        {step.user_answer}
                      </p>
                    </div>
                  )}
                  {step.answer_summary && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-1">
                        Zusammenfassung:
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {step.answer_summary}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </ScrollArea>
  );
};
